import React, { ReactNode, useCallback, useState } from 'react'
import Fuse from 'fuse.js'
import { useQueries, useQuery } from '@tanstack/react-query'
import { SpinnerWrapper } from '../../common/SpinnerWrapper'
import { ErrorView } from '../../../views/error/ErrorView'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { IllustrationChoices } from '../../common/Illustration'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { ResidentUserType } from '../../../state/stores/units'
import { SearchBar } from '../../common/SearchBar'
import { RegisterMemberRequestCard } from './RegisterMemberRequestCard'
import { RegisterMemberPreApprovedCard } from './RegisterMemberPreApprovedCard'
import { ResidentList } from '../ResidentList'
import { unitQueries } from '../../../api/units'
import { userQueries } from '../../../api/users'
import { getUserFullName } from '../../../helpers/user'
import GetToKnow from '../GetToKnow/GetToKnow'
import GettingStarted from '../GettingStarted/GettingStarted'
import { i18nSocietyType } from '../../../i18n'

interface RegisterMemberListProps {
  residentUserType: ResidentUserType
}

export const RegisterMemberList = ({
  residentUserType,
}: RegisterMemberListProps): ReactNode => {
  const [searchString, setSearchString] = useState('')
  const { translate } = useAppTranslation()
  const { society, isLoading: isSocietyLoading } = useCurrentSociety()
  const { data: societyUnits, isLoading: isUnitsLoading } = useQuery(
    unitQueries.getUnitsPerSociety(society?._id)
  )

  const residentIds = new Set(
    societyUnits?.flatMap(
      (unit) =>
        unit?.residentsList
          ?.filter((resident) => {
            if (residentUserType === ResidentUserType.MEMBER) {
              return resident.role === 'member'
            }
            if (residentUserType === ResidentUserType.NON_MEMBER) {
              return resident.role !== 'member'
            }
            return false
          })
          ?.map((resident) => resident.userId) || []
    )
  )
  const { data: societyUsers, isLoading: isLoadingSocietyUsers } = useQueries({
    queries:
      [...residentIds].map((userId) => userQueries.getUser(userId)) || [],
    combine: (queries) => {
      return {
        data: queries.flatMap((query) => query.data || []),
        isLoading: queries.some((query) => query.isLoading),
      }
    },
  })

  const societyUsersWithUnits = societyUsers.map((user) => {
    return {
      user: {
        ...user,
        fullName: getUserFullName(user),
      },
      units:
        societyUnits?.filter((unit) =>
          unit.residentsList?.find((resident) => resident.userId === user._id)
        ) || [],
    }
  })

  const getFilteredUsers = useCallback(() => {
    const options = {
      threshold: 0.2,
      keys: [
        'user.fullName',
        'user.name',
        'user.surname',
        'units.title',
        'units.titleLegal',
      ],
    }

    const fuse = new Fuse(societyUsersWithUnits, options)

    const filteredUsers =
      searchString === ''
        ? societyUsersWithUnits
        : fuse.search(searchString).map((result) => result.item)

    return filteredUsers.map((_user) => _user.user)
  }, [societyUsersWithUnits, searchString])

  const onSearchBarChange = (e: React.FormEvent<HTMLInputElement>): void => {
    setSearchString(e.currentTarget.value)
  }

  const loading = isSocietyLoading || isLoadingSocietyUsers || isUnitsLoading

  if (loading) {
    return <SpinnerWrapper />
  }

  const users = getFilteredUsers()

  return (
    <div className="flex w-full flex-col gap-6">
      <GetToKnow
        title={translate('getToKnowScreen.screens.board.title', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        description={translate('getToKnowScreen.screens.board.description', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        getToKnowId="board"
      />
      <GettingStarted
        title={translate('gettingStartedScreen.screens.members.title', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        description={translate(
          'gettingStartedScreen.screens.members.description',
          { context: i18nSocietyType(society?.societyTypeDisplayName) }
        )}
        gettingStartedId="members"
      />
      <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4">
        <RegisterMemberRequestCard residentUserType={residentUserType} />
        <RegisterMemberPreApprovedCard residentUserType={residentUserType} />
      </div>
      <SearchBar
        className="w-full md:w-80"
        widthClassName="flex grow"
        value={searchString}
        onChange={onSearchBarChange}
      />
      {!loading && users.length > 0 && (
        <ResidentList
          users={users}
          society={society}
          editUserUnitsMode={residentUserType}
          deleteUserOption
        />
      )}
      {!loading && users.length === 0 && searchString === '' && (
        <ErrorView
          title={translate('registerView.emptyState.member.title')}
          illustration={IllustrationChoices.EMPTY}
        />
      )}
      {!loading && users.length === 0 && searchString !== '' && (
        <ErrorView
          title={translate('registerView.emptyState.member.noSearchResults')}
          illustration={IllustrationChoices.EMPTY}
        />
      )}
    </div>
  )
}
